// OnboardingTour.jsx — Carrousel de bienvenue au 1er lancement (web).
// S'affiche une seule fois (localStorage), passable, à la marque de l'asso.
import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Users, CalendarCheck, BookOpen, Palette, ArrowRight, X } from 'lucide-react';

const TOUR_KEY = 'assoperf_tour_v1';

// Le tour de bienvenue est destiné au président et au bureau (gestion de l'asso).
const TOUR_ROLES = ['president', 'vice_president', 'secretaire', 'vice_secretaire', 'tresorier'];

const SLIDES = [
  {
    icon: Users,
    title: 'Vos membres, au même endroit',
    desc: 'Invitez vos adhérents, suivez les cotisations et gérez les rôles du bureau — sans tableur.',
  },
  {
    icon: CalendarCheck,
    title: 'Événements & billetterie',
    desc: 'Créez un événement, vendez des billets, scannez les entrées et suivez les présences.',
  },
  {
    icon: BookOpen,
    title: 'Comptabilité & dons',
    desc: 'Suivez vos recettes et dépenses, exportez vos relevés PDF et éditez les reçus fiscaux CERFA.',
  },
  {
    icon: Palette,
    title: 'À votre image',
    desc: 'Vos couleurs, votre logo, votre nom. Vos membres ne voient que votre identité.',
  },
];

export default function OnboardingTour() {
  const { user } = useAuth();
  const [done, setDone] = useState(() => {
    try { return !!localStorage.getItem(TOUR_KEY); } catch { return true; }
  });
  const [step, setStep] = useState(0);

  if (!user || !TOUR_ROLES.includes(user.role) || done) return null;

  const finish = () => {
    try { localStorage.setItem(TOUR_KEY, '1'); } catch (e) { void e; }
    setDone(true);
  };

  const isLast = step === SLIDES.length - 1;
  const s = SLIDES[step];
  const Icon = s.icon;
  const primary = user?.couleur_principale || '#0047FF';

  return (
    <div className="fixed inset-0 z-[95] bg-[#0F172A]/70 backdrop-blur-md grid place-items-center p-4" data-testid="onboarding-tour">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex justify-end p-4 pb-0">
          <button
            onClick={finish}
            className="text-xs font-semibold text-[#94A3B8] hover:text-[#0F172A] inline-flex items-center gap-1"
            data-testid="onboarding-skip"
          >
            Passer <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="px-8 pb-8 pt-2 text-center">
          <div
            className="w-16 h-16 rounded-2xl grid place-items-center mx-auto mb-6"
            style={{ background: `${primary}1A`, color: primary }}
          >
            <Icon className="w-8 h-8" />
          </div>
          <h2 className="font-display text-2xl font-bold text-[#0F172A] mb-3">{s.title}</h2>
          <p className="text-sm text-[#475569] leading-relaxed min-h-[60px]">{s.desc}</p>

          <div className="flex items-center justify-center gap-2 my-7">
            {SLIDES.map((_, i) => (
              <span
                key={i}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === step ? 24 : 8,
                  background: i === step ? primary : '#E2E8F0',
                }}
              />
            ))}
          </div>

          <button
            onClick={() => (isLast ? finish() : setStep(step + 1))}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-white font-bold text-base transition-all shadow-md hover:-translate-y-0.5"
            style={{ background: primary }}
            data-testid="onboarding-next"
          >
            {isLast ? 'Commencer' : 'Suivant'}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
